import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Github, Globe, ExternalLink, Calendar, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import TransitionLink from '../components/layout/TransitionLink';
import { projects, Project } from '../data/projects';
import SEO from '../components/seo/SEO';
import ProjectThumb from '../components/optimus/thumbs';

interface ProjectDetailsPageProps {
  darkMode: boolean;
}

// featuredProjects.* i18n keys for each project id (title|description)
const translationKeys: Record<number, string> = {
  1: 'aiPoweredPortfolio|aiPortfolioDescription',
  2: 'securityTitle|securityDescription',
  3: 'noteriousTitle|noteriousDescription',
  4: 'lexifAITitle|lexifAIDescription',
  5: 'handwritingRecognitionTitle|handwritingRecognitionDescription',
  6: 'aiTemplateGeneratorTitle|aiTemplateGeneratorDescription',
  7: 'animatedBackgroundTitle|animatedBackgroundDescription',
  8: 'ipLoggingSystemTitle|ipLoggingSystemDescription',
  9: 'neuralNetworkAcceleratorTitle|neuralNetworkAcceleratorDescription',
  10: 'nlpApiTitle|nlpApiDescription',
  11: 'interactiveTerminalTitle|interactiveTerminalDescription',
};

/**
 * Project detail page in the Optimus design system. Fully responsive and fully
 * translated: title/description come from the existing featuredProjects keys,
 * and the curated highlights / challenge / outcome copy lives under op.proj.*
 * in both locales. Replaces the old bullet-wall layout and legacy Results
 * showcase components.
 */
// darkMode is accepted for route compatibility; the page is fully driven by the
// op design tokens, which flip with the .dark class on <html>.
const ProjectDetailsPage: React.FC<ProjectDetailsPageProps> = () => {
  const { t, i18n } = useTranslation();
  const { projectId, slug } = useParams<{ projectId: string; slug: string }>();
  const [project, setProject] = useState<Project | null>(null);

  useEffect(() => {
    let found: Project | undefined;
    if (projectId) {
      const id = parseInt(projectId);
      if (!isNaN(id)) found = projects.find((p) => p.id === id) as Project;
    } else if (slug) {
      found = projects.find((p) => p.slug === slug) as Project;
    }

    if (found) {
      setProject(found);
      if (projectId && !slug) {
        window.history.replaceState(null, '', `/project/${found.slug}`);
      }
    } else {
      window.location.href = '/projects';
    }
  }, [projectId, slug]);

  if (!project) {
    return (
      <div className="op-page min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  const [titleKey, descKey] = (translationKeys[project.id] || '|').split('|');
  const title = titleKey ? t(`featuredProjects.${titleKey}`) : project.title;
  const description = descKey ? t(`featuredProjects.${descKey}`) : project.description;

  // Split the title so the tail renders in the muted serif tone
  const titleWords = title.split(' ');
  const titleHead = titleWords.slice(0, 2).join(' ');
  const titleTail = titleWords.slice(2).join(' ');

  const highlights = [1, 2, 3, 4].map((n) => ({
    title: t(`op.proj.p${project.id}.h${n}t`),
    description: t(`op.proj.p${project.id}.h${n}d`),
  }));
  const challenge = t(`op.proj.p${project.id}.challenge`);
  const outcome = t(`op.proj.p${project.id}.outcome`);

  const categoryLabel =
    project.category === 'web' ? t('projectDetails.webDevelopment')
    : project.category === 'ml' ? t('projectDetails.machineLearning')
    : project.category === 'hardware' ? t('projectDetails.hardwareDesign')
    : project.category === 'security' ? t('projectDetails.securityEngineering')
    : t('projectDetails.other');

  const projectSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareSourceCode',
    name: project.title,
    description: project.description,
    image: project.image,
    codeRepository: project.codeUrl,
    programmingLanguage: project.tags,
    author: { '@type': 'Person', name: 'Talal Alkhaled', url: 'https://talkhaled.com' },
    datePublished: project.completedDate,
    url: `https://talkhaled.com/project/${project.slug}`,
  };

  return (
    <main className="op-page noise-overlay relative min-h-screen overflow-x-hidden">
      <SEO
        title={`${title} | Talal Alkhaled`}
        description={description.slice(0, 155)}
        url={`https://talkhaled.com/project/${project.slug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(projectSchema)}</script>
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 lg:pt-40 pb-24 lg:pb-32">
        {/* Back link */}
        <TransitionLink
          to="/projects"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12 group"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180 transition-transform group-hover:-translate-x-1 rtl:group-hover:translate-x-1" />
          {t('projectDetails.backToProjects')}
        </TransitionLink>

        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Main column */}
          <div className="lg:col-span-2 min-w-0">
            {/* Header */}
            <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-6">
              <span className="w-8 h-px bg-foreground/30" />
              {categoryLabel}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display tracking-tight leading-[1.02] mb-6">
              {titleHead}
              {titleTail && (
                <>
                  {' '}
                  <span className="text-muted-foreground">{titleTail}</span>
                </>
              )}
            </h1>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-10">
              {project.tags.slice(0, 5).map((tag) => (
                <span key={tag} className="px-3 py-1.5 border border-foreground/15 text-xs font-mono text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>

            {/* Hero illustration */}
            <div className="border border-foreground/10 bg-white mb-12 overflow-hidden">
              <div className="w-full aspect-video">
                <ProjectThumb
                  slug={project.slug}
                  title={title}
                  image={project.image}
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Overview */}
            <section className="mb-16">
              <h2 className="text-2xl lg:text-3xl font-display tracking-tight mb-4">
                {t('projectDetails.aboutThisProject')}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{description}</p>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-8">
                <span className="w-8 h-px bg-foreground/30" />
                {t('op.projPage.highlights')}
              </span>
              <div>
                {highlights.map((h, i) => (
                  <div
                    key={i}
                    className="flex gap-6 lg:gap-10 py-6 border-b border-foreground/10 last:border-b-0 group"
                  >
                    <span className="shrink-0 font-mono text-sm text-muted-foreground pt-1">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="text-xl lg:text-2xl font-display mb-2 group-hover:translate-x-1 transition-transform duration-300">
                        {h.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">{h.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Challenge + Outcome */}
            <section className="grid md:grid-cols-2 gap-px bg-foreground/10 border border-foreground/10">
              <div className="bg-background p-8 border-t-2 border-t-negative/60">
                <span className="block font-mono text-xs tracking-widest uppercase text-negative mb-4">
                  {t('op.projPage.challenge')}
                </span>
                <p className="text-muted-foreground leading-relaxed">{challenge}</p>
              </div>
              <div className="bg-background p-8 border-t-2 border-t-positive/60">
                <span className="block font-mono text-xs tracking-widest uppercase text-positive mb-4">
                  {t('op.projPage.outcome')}
                </span>
                <p className="text-muted-foreground leading-relaxed">{outcome}</p>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div>
            <div className="lg:sticky lg:top-28 border border-foreground/10 p-8">
              <h3 className="text-lg font-medium mb-6 text-foreground">
                {t('projectDetails.projectDetails')}
              </h3>

              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-3">
                  <div className="p-2 border border-foreground/10">
                    <Tag size={18} className="text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('projectDetails.category')}</p>
                    <p className="font-medium text-foreground">{categoryLabel}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 border border-foreground/10">
                    <Calendar size={18} className="text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('projectDetails.completed')}</p>
                    <p className="font-medium text-foreground" dir="ltr">
                      {new Date(project.completedDate).toLocaleDateString(
                        i18n.language.startsWith('ar') ? 'ar-EG' : 'en-US',
                        { year: 'numeric', month: 'long', day: 'numeric' }
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <hr className="my-6 border-foreground/10" />

              <div className="space-y-4">
                <a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full text-sm font-medium bg-foreground text-background hover:bg-foreground/90 transition-colors"
                >
                  <Globe size={16} />
                  {t('projectDetails.viewLiveDemo')}
                  <ExternalLink size={14} />
                </a>
                <a
                  href={project.codeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full text-sm font-medium border border-foreground/20 text-foreground hover:bg-foreground/5 transition-colors"
                >
                  <Github size={16} />
                  {t('projectDetails.viewSourceCode')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProjectDetailsPage;
